<template>
  <main id="mainContent" class="confirmation">
    <Stepper :step="stepNum"/>
    <Timer ref="timer" />
    <div class="app-main-content">
      <h1>{{ screenData.title }}</h1>
      <p>{{ screenData.description }}</p>

      <div class="app-content-panels">
        <div class="app-panel-left" v-if="user">
          <template v-if="user?.isBusinessCustomer">
            <div class="info-group">
              <div class="app-label-non-control">Organization name</div>
              <span class="notranslate">{{ user?.organizationName }}</span>
            </div>
          </template>
          <template v-else>
            <div class="info-group">
              <div class="app-label-non-control">Name</div>
              <span class="notranslate">{{ fullName }}</span>
            </div>
            <div class="info-group">
              <div class="app-label-non-control">Date of birth</div>
              <div class="hiddable">
                <span aria-hidden="true"><span class="notranslate">{{ myDob }}</span></span>
                <span class="sr-only">{{ user?.dateOfBirth }}</span>
                <button class="app-btn-image" @click="toggleHidden('dob')">
                  <img src="@ridmv/web-themes/images/icons/eye-solid.svg" alt="" aria-hidden="true">
                  <span class="sr-only">Show or hide date of birth</span>
                </button>
              </div>
            </div>
            <div class="info-group" v-if="myLicense.length > 0">
              <div class="app-label-non-control">{{ resTypeForCustomer && (resTypeForCustomer.visitTypeId == 35 || resTypeForCustomer.visitTypeId == 36 || resTypeForCustomer.visitTypeId == 37 ) ? 'RI Taxation ID number' : 'RI license number' }}</div>
              <div class="hiddable">
                <span aria-hidden="true"><span class="notranslate">{{ myLicense }}</span></span>
                <span class="sr-only">{{ user?.licenseNumber }}</span>
                <button class="app-btn-image" @click="toggleHidden('license')">
                  <img src="@ridmv/web-themes/images/icons/eye-solid.svg" alt="" aria-hidden="true">
                  <span class="sr-only">Show or hide license number</span>
                </button>
              </div>
            </div>
          </template>
          <div class="info-group" v-if="myPlate.length > 0">
            <div class="app-label-non-control">License plate number</div>
            <div class="hiddable">
              <span aria-hidden="true"><span class="notranslate">{{ myPlate }}</span></span>
              <span class="sr-only">{{ user?.plateNumber }}</span>
              <button class="app-btn-image" @click="toggleHidden('plate')">
                <img src="@ridmv/web-themes/images/icons/eye-solid.svg" alt="" aria-hidden="true">
                <span class="sr-only">Show or hide license plate number</span>
              </button>
            </div>
          </div>
          <div class="info-group"
               v-if="user.reservationSendConfirmationRequest && user.reservationSendConfirmationRequest.phone.length > 0">
            <div class="app-label-non-control">Phone number</div>
            <span class="notranslate">{{ formatPhoneForRims(user?.reservationSendConfirmationRequest?.phone) }}</span>
          </div>
          <div class="info-group"
               v-if="user.reservationSendConfirmationRequest && user.reservationSendConfirmationRequest.email.length > 0">
            <div class="app-label-non-control">Email</div>
            <span class="notranslate">{{ user?.reservationSendConfirmationRequest?.email }}</span>
          </div>
          <div class="info-group" v-if="user.reservationSendConfirmationRequest">
            <div class="app-label-non-control">Confirmation and reminder</div>
            <span class="notranslate">{{ user?.reservationSendConfirmationRequest?.isConsentProvided ? 'Yes' : 'No' }}</span>
          </div>
        </div>

        <!-- right panel -->
        <ServiceInfo />

        <!-- bottom panel -->
        <AsideForTerms :verify="termsAccepted" @doResetVerify="termsAccepted = false" @doValidated="routeToNext"/>

        <BaseButtonGroup>
          <button type="button" @click="handlePseudoRouting(true)">{{ language.btnPrev }}</button>
          <button type="button" class="app-btn-primary" @click="handlePseudoRouting(false)">
            {{ (!isModify && resFeeApplicable) ? language.btnNext : language.btnConfirmRes }}
          </button>
        </BaseButtonGroup>
      </div>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import appConfig from '@/app.config';
import Stepper from '@/components/reservation/Stepper.vue';
import Timer from '@/components/reservation/Timer.vue';
import ServiceInfo from '@/components/reservation/confirmation/ServiceInfo.vue';
import AsideForTerms from '@/components/reservation/aside/AsideForTerms.vue';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup.vue';
import { SLOTS_HOLD_CREATE,} from '@/store/_commons';
import { confirmationMixin } from '@/utils/confirmationMixin';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';
import { useHead } from '@unhead/vue';

// Use unhead to manage the page meta
useHead({
  title: `Confirm reservation | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Review and agree to the RI DMV terms before submitting your reservation request.'
    }
  ]
}) 

// Vuex store
const store = useStore();

// Vue router
const router = useRouter();
const route = useRoute();

// Composables
const { getPrettyDob, getHiddenValue } = confirmationMixin();
const { formatPhoneForRims } = usePhoneFormatter();

// Reactive data refs
const stepNum = ref(4);
const screenData = ref({});
const termsAccepted = ref(false);
const isModify = ref(false);

const timer = ref(null);

const myDob = ref('');
const myLicense = ref('');
const myPlate = ref('');
const myDobHidden = ref('');
const myDobPretty = ref('');
const myLicenseHidden = ref('');
const myPlateHidden = ref('');
const user = ref({});

// Computed properties
const language = computed(() => store.state.user.LANG);
const STEPS = computed(() => store.state.user.STEPS);
const PAGES = computed(() => store.state.user.PAGES);
const reservationSlotId = computed(() => store.state.user.reservationSlotId);
const resDetails = computed(() => store.state.user.resDetails);
const resAppointmentBarcode = computed(() => store.state.user.resAppointmentBarcode);
const resFeeApplicable = computed(() => store.state.user.resFeeApplicable);
const resTypeForCustomer = computed(() => store.state.user.resTypeForCustomer);
const resSlotHoldEndTime = computed(() => store.state.user.resSlotHoldEndTime);

const fullName = computed(() => {
  const parts = [
    user.value?.firstName,
    user.value?.middleName,
    user.value?.lastName
  ];
  
  // Filter out empty or null values and join with a single space
  const existingParts = parts.filter(part => part && part.trim().length > 0);
  
  return existingParts.join(' ');
});

// Watcher
watch(resAppointmentBarcode, (newValue) => {
  if (newValue?.length > 0) {
    router.push({ name: 'ReservationFinal' });
  }
});

// created logic

//Methods
const createSlotHold = (isExtendSlot) => store.dispatch(SLOTS_HOLD_CREATE, isExtendSlot);

const createOrUpdate = async () => {
  // Add additional 30 sec to the hold time to allow for processing, if current time is less than 30 sec
  const end = resSlotHoldEndTime.value;
  const now = new Date().getTime();
  if (now + 30000 > end) {
    resSlotHoldEndTime.value = end + 30000;
    await createSlotHold(true);
  }

  if (isModify.value) {
    await store.dispatch('reservation/updateReservation'); 
  } else {
    const args = {
      onlineReservationCreateReservationRequest: {
        reservationSlotId: reservationSlotId.value,
        reservationUserIdentifier: store.state.user.reservationUserId,
        visitTypeId: resTypeForCustomer.value.visitTypeId,   // TODO: replace with resTypeForCustomer.visitTypeId
        isFeeApplicable: resTypeForCustomer.value.isFeeApplicable,  // TODO: replace with resTypeForCustomer.isFeeApplicable 
      },
    };
    await store.dispatch('reservation/createReservation', args);  // no payment fee detail in non-road test 
  }
};

const handlePseudoRouting = (isPrev) => {
  if (isPrev) {
    if (route.query.from === 'modifyExisting') {
      store.dispatch('user/setStoreProperty', { property: 'resSelectedForUpdate', value: {} });
      store.dispatch('user/setStoreProperty', { property: 'modifyReservation', value: false });
      const userInfo = store.state.user.resDetails?.userInfo;
      if(userInfo?.reservationSendConfirmationRequest) {
        store.dispatch('user/setReservationDetails', [{ 
          property: 'userInfo', 
          value: {
            ...userInfo,
            reservationSendConfirmationRequest: {
              ...userInfo.reservationSendConfirmationRequest,
              isModifyRequest: false,
            }
          }
        }]);
      }
      router.push({ name: PAGES.value.updateRes, query: { from: 'newres' } });
      return;
    }
    if(route.query.from === 'newres') {
      router.push({ name: PAGES.value.updateRes, query: { from: 'newres' } });
      return;

    }
    router.push({ name: PAGES.value.userInfo });
  } else {
    // have child component initiate checkbox validation
    // actual routing is determined by child's emit (to @doValidated)
    termsAccepted.value = true;
  }
};

/**
 * Special handling of hidden/extra-formatting fields.
 */
const initUserInfo = () => {
  user.value = resDetails.value?.userInfo;

  if (!user.value.isBusinessCustomer) {
    myDobPretty.value = getPrettyDob(user.value.dateOfBirth);
    myDob.value = myDobHidden.value = getHiddenValue(myDobPretty.value);
    myLicense.value = myLicenseHidden.value = getHiddenValue(user.value.licenseNumber);
  }
  myPlate.value = myPlateHidden.value = getHiddenValue(user.value.plateNumber);
};

/**
 * Another route check due to added payment option.
 */
const routeToNext = () => {
  if (resFeeApplicable.value && !isModify.value) {
    if (user.value.customerId && user.value.cdlRoadTestEligibilityDate) {
      router.push({ name: PAGES.value.payment });
    } else {
      console.warn('isFeeApplicable=true but there is no customer ID, ' +
              'cdlRoadTestEligibilityDate and/or error.', user.value);
    }
  } else {
    createOrUpdate();
  }
};

const toggleHidden = (btn) => {  
  switch (btn) {
    case 'dob':
      // Toggle between the hidden and pretty versions
      myDob.value = (myDob.value.indexOf('*') > -1) ? myDobPretty.value : myDobHidden.value;
      break;
    case 'license':
      // Toggle between the hidden and actual versions
      myLicense.value = (myLicense.value.indexOf('*') > -1) ? user.value.licenseNumber : myLicenseHidden.value;
      break;
    case 'plate':
      // Toggle between the hidden and actual versions
      myPlate.value = (myPlate.value.indexOf('*') > -1) ? user.value.plateNumber : myPlateHidden.value;
      break;
    default:
      // Nada
  }
};

onMounted(async () => {
  if (reservationSlotId.value > 0) {
  screenData.value = STEPS.value[stepNum.value];
  // initUserInfo() logic would be placed here or in `onMounted`
  initUserInfo();
  } else {
    router.push({ name: 'UserInformation' });
  }

  isModify.value = await store.dispatch('user/isReservationForUpdate');
});
</script>


<style scoped lang="scss">
.confirmation {
  .info-group {
    margin: 0 1em 1.25em 0;
    button {
      img {
        width: 18px;
      }
    }
  }
  .hiddable {
    display: flex;
    margin-top: -0.5em;
    margin-bottom: -0.5em;
    span {
      margin-top: 0.7em;
    }
    button {
      margin-left: 0.25em;
    }
  }
}

.app-button-group {
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>
